// PRE RMA — Module PEV (Tableau 14 : Vaccination).
// Source : pre_rma_pev.html (cibles 3,75 % / 3,58 %, antigènes, DTC1/DTC3 mensuels).
// Toutes les fonctions sont pures : mêmes résultats côté écran, API et export.

export const PEV_ANTIGENES = ['bcg', 'vpo0', 'vpo1', 'vpo2', 'vpo3', 'var1', 'var2', 'td'] as const;
export type PevAntigene = (typeof PEV_ANTIGENES)[number];

export const PEV_ANTIGENE_LABELS: Record<PevAntigene, string> = {
  bcg: 'BCG', vpo0: 'VPO0', vpo1: 'VPO1 / DTC-HepB-Hib1', vpo2: 'VPO2 / DTC-HepB-Hib2',
  vpo3: 'VPO3 / DTC-HepB-Hib3', var1: 'VAR1', var2: 'VAR2', td: 'Td (femmes enceintes)',
};

export const PEV_STRATEGIES = ['fixe', 'avancee', 'mobile'] as const;
export type PevStrategie = (typeof PEV_STRATEGIES)[number];
export const PEV_STRATEGIE_LABELS: Record<PevStrategie, string> = { fixe: 'Fixe', avancee: 'Avancée', mobile: 'Mobile' };

export const PEV_TRANCHES = ['0-11', '12-59'] as const;
export type PevTranche = (typeof PEV_TRANCHES)[number];
export const PEV_TRANCHE_LABELS: Record<PevTranche, string> = { '0-11': '0-11 mois', '12-59': '12-59 mois' };

export const TAUX_CIBLE_NAISSANCE = 0.0375; // naissances vivantes
export const TAUX_CIBLE_0_11_MOIS = 0.0358; // enfants de 0 à 11 mois

export interface PevSeance {
  semaine: string;
  dateSeance: string | null; // jj/mm ou ISO — affichage uniquement
  strategie: PevStrategie;
  tranche: PevTranche;
  doses: Partial<Record<PevAntigene, number>>;
}

const n = (x: unknown): number => {
  const v = typeof x === 'number' ? x : parseInt(String(x ?? ''), 10);
  return Number.isFinite(v) && v > 0 ? v : 0;
};

export interface ValidationSimple { ok: boolean; erreurs: string[] }

export function validerSeancePev(s: Partial<PevSeance>): ValidationSimple {
  const erreurs: string[] = [];
  if (!s.semaine || !String(s.semaine).trim()) erreurs.push('La semaine est obligatoire (ex. S1).');
  if (!s.strategie || !PEV_STRATEGIES.includes(s.strategie)) erreurs.push('Stratégie invalide (Fixe, Avancée ou Mobile).');
  if (!s.tranche || !PEV_TRANCHES.includes(s.tranche)) erreurs.push("Tranche d'âge invalide (0-11 ou 12-59 mois).");
  for (const [k, v] of Object.entries(s.doses ?? {})) {
    if (!PEV_ANTIGENES.includes(k as PevAntigene)) erreurs.push(`Antigène inconnu : ${k}.`);
    else if (!Number.isInteger(v) || (v as number) < 0 || (v as number) > 9999) erreurs.push(`${k} : valeur entière entre 0 et 9999.`);
  }
  if (s.strategie && s.tranche && !Object.values(s.doses ?? {}).some((v) => (v as number) > 0)) {
    erreurs.push('Saisissez au moins un enfant/femme vacciné(e) pour cette séance.');
  }
  return { ok: erreurs.length === 0, erreurs };
}

/** Total vaccinés par antigène, toutes séances confondues. */
export function totauxParAntigene(seances: PevSeance[]): Record<PevAntigene, number> {
  const t = Object.fromEntries(PEV_ANTIGENES.map((a) => [a, 0])) as Record<PevAntigene, number>;
  for (const s of seances) for (const a of PEV_ANTIGENES) t[a] += n(s.doses[a]);
  return t;
}

/** Ventilation Stratégie × Tranche (Fixe/Avancée/Mobile × 0-11/12-59) pour un antigène. */
export function ventilationStrategie(seances: PevSeance[], antigene: PevAntigene) {
  const r: Record<string, number> = {};
  for (const st of PEV_STRATEGIES) for (const tr of PEV_TRANCHES) r[`${st}|${tr}`] = 0;
  for (const s of seances) r[`${s.strategie}|${s.tranche}`] += n(s.doses[antigene]);
  return r;
}

/** Cibles mensuelles arrondies à l'entier (formules du fichier PEV, population totale annuelle). */
export function ciblesMensuelles(popTotale: number) {
  const pop = n(popTotale);
  const naiss = Math.round((pop * TAUX_CIBLE_NAISSANCE) / 12);
  const m011 = Math.round((pop * TAUX_CIBLE_0_11_MOIS) / 12);
  return {
    naissance: naiss, zeroOnze: m011,
    parAntigene: { bcg: naiss, vpo0: naiss, vpo1: m011, vpo2: m011, vpo3: m011, var1: m011, var2: m011, td: 0 } as Record<PevAntigene, number>,
  };
}
export const cibleAnnuelle0_11 = (popTotale: number) => n(popTotale) * TAUX_CIBLE_0_11_MOIS;

/** Taux de couverture en % (1 décimale). Cible 0 → null (jamais de division par zéro). */
export function tauxCouverture(vaccines: number, cible: number): number | null {
  return cible > 0 ? Math.round((vaccines / cible) * 1000) / 10 : null;
}

/**
 * Taux d'abandon = (DTC1 − DTC3) / DTC1 × 100. DTC1 = 0 → null.
 * Négatif possible (DTC3 > DTC1) : signalé comme anomalie par l'appelant.
 */
export function tauxAbandon(dtc1: number, dtc3: number): number | null {
  const a = n(dtc1);
  return a > 0 ? Math.round(((a - n(dtc3)) / a) * 1000) / 10 : null;
}
export const SEUIL_ABANDON_ALERTE = 10; // % — au-delà : alerte visuelle (seuil usuel PEV)

// ---------------------------------------------------------------------
// Indicateurs mensuels DTC1 et catégorisation de la période (suivi PEV)
// ---------------------------------------------------------------------
/** Seuil du TCV DTC1 (en %) : « > 90 » = bonne couverture. */
export const SEUIL_TCV_DTC1 = 90;
/** Seuil du taux d'abandon (en %) : « < 10 » = abandon acceptable. */
export const SEUIL_TAB_CATEGORIE = SEUIL_ABANDON_ALERTE;

/**
 * TCV DTC1 mensuel (%) = DTC1 vaccinés du mois / cible mensuelle des 0-11 mois × 100 (1 décimale).
 * Cible absente (population non renseignée) → null : jamais de division par zéro, jamais de valeur inventée.
 */
export function tcvDtc1Mensuel(dtc1: number | null | undefined, cibleMensuelle0_11: number): number | null {
  return tauxCouverture(n(dtc1), cibleMensuelle0_11);
}

export type CategoriePevNumero = 1 | 2 | 3 | 4;
export type CouleurCategoriePev = 'VERT' | 'JAUNE' | 'ROUGE' | 'ROUGE_FONCE';
export interface CategoriePeriodePev {
  categorie: CategoriePevNumero;
  couleur: CouleurCategoriePev;
  libelle: string;
  description: string;
  tcvDtc1: number;
  tab: number;
}

/**
 * Catégorisation de la période — 4 règles strictes (TCV DTC1 et TAB en %, tels qu'affichés, 1 décimale) :
 *  1 VERT         : TCV DTC1 > 90 et TAB < 10
 *  2 JAUNE        : TCV DTC1 > 90 et TAB >= 10
 *  3 ROUGE        : TCV DTC1 <= 90 et TAB < 10
 *  4 ROUGE FONCÉ  : TCV DTC1 <= 90 et TAB >= 10
 * Retourne null si l'un des deux indicateurs n'est pas calculable (cible ou DTC1 absent) : on n'invente pas de catégorie.
 */
export function categoriserPeriodePev(tcvDtc1: number | null | undefined, tab: number | null | undefined): CategoriePeriodePev | null {
  if (tcvDtc1 === null || tcvDtc1 === undefined || tab === null || tab === undefined) return null;
  if (!Number.isFinite(tcvDtc1) || !Number.isFinite(tab)) return null;
  const bonneCouverture = tcvDtc1 > SEUIL_TCV_DTC1;
  const abandonEleve = tab >= SEUIL_TAB_CATEGORIE;
  const base = { tcvDtc1, tab };
  if (bonneCouverture && !abandonEleve) return { ...base, categorie: 1, couleur: 'VERT', libelle: 'Catégorie 1', description: `TCV DTC1 > ${SEUIL_TCV_DTC1} % et taux d'abandon < ${SEUIL_TAB_CATEGORIE} %` };
  if (bonneCouverture && abandonEleve) return { ...base, categorie: 2, couleur: 'JAUNE', libelle: 'Catégorie 2', description: `TCV DTC1 > ${SEUIL_TCV_DTC1} % et taux d'abandon ≥ ${SEUIL_TAB_CATEGORIE} %` };
  if (!bonneCouverture && !abandonEleve) return { ...base, categorie: 3, couleur: 'ROUGE', libelle: 'Catégorie 3', description: `TCV DTC1 ≤ ${SEUIL_TCV_DTC1} % et taux d'abandon < ${SEUIL_TAB_CATEGORIE} %` };
  return { ...base, categorie: 4, couleur: 'ROUGE_FONCE', libelle: 'Catégorie 4', description: `TCV DTC1 ≤ ${SEUIL_TCV_DTC1} % et taux d'abandon ≥ ${SEUIL_TAB_CATEGORIE} %` };
}

export interface PevMensuel { dtc1: number | null; dtc3: number | null; pfa?: number | null; rougeole?: number | null; tnn?: number | null }

export interface PointCourbe {
  mois: number; // 1..12
  dtc1Mois: number; dtc3Mois: number;
  dtc1Cumul: number; dtc3Cumul: number;
  cibleCumul: number;
  couvertureDtc1: number | null; couvertureDtc3: number | null;
  abandonCumul: number | null;
  /** TCV DTC1 et taux d'abandon DU MOIS (non cumulés) — base de la catégorisation de la période. */
  tcvDtc1Mois: number | null;
  abandonMois: number | null;
  saisi: boolean;
}

/**
 * Courbe de couverture cumulée : axe X = mois (1-12), axe Y = enfants vaccinés cumulés.
 * Les mois sans saisie ne comptent pas (la courbe s'arrête au dernier mois saisi) mais
 * la ligne de cible continue sur 12 mois.
 */
export function courbeCumulee(parMois: Array<PevMensuel | null | undefined>, popTotale: number): PointCourbe[] {
  const cibleMois = cibleAnnuelle0_11(popTotale) / 12;
  let c1 = 0, c3 = 0;
  return Array.from({ length: 12 }, (_, i) => {
    const m = parMois[i];
    const saisi = !!m && (m.dtc1 !== null && m.dtc1 !== undefined || m.dtc3 !== null && m.dtc3 !== undefined);
    const d1 = n(m?.dtc1), d3 = n(m?.dtc3);
    c1 += d1; c3 += d3;
    const cibleCumul = Math.round(cibleMois * (i + 1));
    return {
      mois: i + 1, dtc1Mois: d1, dtc3Mois: d3, dtc1Cumul: c1, dtc3Cumul: c3, cibleCumul,
      couvertureDtc1: tauxCouverture(c1, cibleCumul), couvertureDtc3: tauxCouverture(c3, cibleCumul),
      abandonCumul: tauxAbandon(c1, c3),
      tcvDtc1Mois: tcvDtc1Mensuel(d1, Math.round(cibleMois)), abandonMois: tauxAbandon(d1, d3), saisi,
    };
  });
}

export function anomaliesPev(m: PevMensuel): string[] {
  const a: string[] = [];
  if (n(m.dtc3) > n(m.dtc1)) a.push('DTC3 supérieur à DTC1 : taux d\'abandon négatif, vérifiez les deux valeurs.');
  const ab = tauxAbandon(n(m.dtc1), n(m.dtc3));
  if (ab !== null && ab > SEUIL_ABANDON_ALERTE) a.push(`Taux d'abandon élevé (${ab} % > ${SEUIL_ABANDON_ALERTE} %).`);
  return a;
}

export function validerPevMensuel(m: Partial<PevMensuel>): ValidationSimple {
  const erreurs: string[] = [];
  for (const k of ['dtc1', 'dtc3', 'pfa', 'rougeole', 'tnn'] as const) {
    const v = m[k];
    if (v !== null && v !== undefined && (!Number.isInteger(v) || v < 0 || v > 99999)) erreurs.push(`${k.toUpperCase()} : entier entre 0 et 99999.`);
  }
  return { ok: erreurs.length === 0, erreurs };
}

/**
 * Suivi mensuel DTC1 / DTC3 EFFECTIF : valeur saisie, sinon reprise AUTOMATIQUE des séances de vaccination
 * (VPO1 = DTC-HepB-Hib1, VPO3 = DTC-HepB-Hib3, comme dans le fichier PEV d'origine) → l'agent ne saisit qu'une fois.
 */
export function mensuelEffectif(m: PevMensuel | null | undefined, seances: PevSeance[]): PevMensuel | null {
  const t = totauxParAntigene(seances);
  const dtc1 = m?.dtc1 ?? (seances.length ? t.vpo1 : null);
  const dtc3 = m?.dtc3 ?? (seances.length ? t.vpo3 : null);
  if (!m && dtc1 === null && dtc3 === null) return null;
  return { ...(m ?? {}), dtc1, dtc3 } as PevMensuel;
}
